/* CreditTools.js — پنل‌های تکمیلی فرم «وصول سپرده»
   ۱. FromDocumentPanel : «دريافت اطلاعات از تنظیم حساب» + «ثبت سپرده های کسر شده اسناد حسابداری»
   ۲. UpLevelPanel      : «تعیین بستانکاری خزانه» + «تایید»
   همهٔ کوئری‌ها و محاسبات در بک‌اند (deposit/views/credit_tools.py) است. */
import React, { useState, useEffect } from 'react';
import { jsonOf, netErr } from '../../utils/http';

const toFa   = n => n === null || n === undefined ? '' : String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v = Number(n || 0); return v === 0 ? '۰' : v.toLocaleString('fa-IR'); };
const API = (path, opts = {}) => fetch(`/api/deposit${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` }, ...opts,
}).then(jsonOf, netErr);

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal: { background: '#fff', borderRadius: '14px', width: '100%', maxHeight: '92vh', display: 'flex',
    flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px',
    background: 'linear-gradient(135deg,#0a1628,#1a2f50)', borderRadius: '14px 14px 0 0', color: '#fff',
    fontWeight: 700, fontSize: '14px' },
  close: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
    width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer' },
  body: { overflowY: 'auto', flex: 1, padding: '16px' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 18px', alignItems: 'center',
    borderTop: '1px solid #eee', background: '#f8f9fa', borderRadius: '0 0 14px 14px' },
  th: { padding: '8px', background: '#1a2f50', color: '#fff', fontSize: '11px', whiteSpace: 'nowrap',
    position: 'sticky', top: 0 },
  td: { padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center' },
  err: { background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '8px', padding: '10px 14px',
    color: '#c62828', marginBottom: '10px', fontSize: '13px' },
  btn: { padding: '8px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '13px', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#1b5e20,#2e7d32)' },
  btnGray: { padding: '8px 18px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: '13px', background: '#e0e0e0', color: '#333' },
  input: { padding: '8px 10px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '13px',
    fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  label: { fontSize: '12px', fontWeight: 600, color: '#555' },
};

function Shell({ title, onClose, children, footer, width }) {
  // کلیک راست روی پنل آن را می‌بندد
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={{ ...st.modal, maxWidth: width }} onClick={e => e.stopPropagation()}
        onContextMenu={e => { e.preventDefault(); onClose(); }}>
        <div style={st.head}><span>{title}</span><button onClick={onClose} style={st.close}>✕</button></div>
        <div style={st.body}>{children}</div>
        {footer && <div style={st.foot}>{footer}</div>}
      </div>
    </div>
  );
}

/* ═══════════ ۱. دريافت اطلاعات از تنظیم حساب ═══════════ */
const DOC_COLS = ['سند', 'تاریخ', 'شرح سند', 'شرح ریز سند', 'قرارداد', 'تاریخ قرارداد', 'پیمانکار',
  'مبلغ سپرده', 'موضوع سپرده', 'شماره چک', 'تاریخ وصول چک'];

export function FromDocumentPanel({ credit, onClose, onSaved }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API(`/credits/${credit.id}/from-document/`).then(j => { if (j.ok) setRes(j); else setErr(j.error || j.msg); });
  }, [credit.id]);

  const save = async () => {
    setBusy(true);
    const j = await API(`/credits/${credit.id}/from-document/save/`, { method: 'POST' });
    setBusy(false);
    if (j.ok) onSaved(); else setErr(j.error || j.msg);
  };

  // ستونی که در همهٔ ردیف‌ها خالی یا صفر است پنهان می‌شود
  const rows = res?.data || [];
  const cols = DOC_COLS.filter(c => rows.some(r => r[c] !== '' && r[c] !== 0 && r[c] !== null && r[c] !== undefined));

  return (
    <Shell title={`دريافت اطلاعات از تنظیم حساب — بستانکاری ${toFa(credit.credit_no)}/${toFa(credit.credit_year)}`}
      onClose={onClose} width="1200px"
      footer={res && <>
        <span style={{ marginLeft: 'auto', fontSize: '12px' }}>
          تعداد: <b>{toFa(rows.length)}</b> &nbsp;&nbsp; جمع مبلغ سپرده: <b>{numFmt(res.sum_amount)}</b>
        </span>
        <button onClick={onClose} style={st.btnGray}>بستن</button>
        <button onClick={save} disabled={busy} style={st.btn}>{busy ? '⏳...' : 'ثبت سپرده های کسر شده اسناد حسابداری'}</button>
      </>}>
      {err && <div style={st.err}>{err}</div>}
      {!res && !err && <div>⏳</div>}
      {res && (
        <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{cols.map(c => <th key={c} style={st.th}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={r.DocumentDetailId || k} style={{ background: k % 2 ? '#fdf6e3' : '#fff' }}>
                  {cols.map(c => (
                    <td key={c} style={{ ...st.td, textAlign: c === 'مبلغ سپرده' ? 'left' : c.startsWith('شرح') ? 'right' : 'center',
                      whiteSpace: c.startsWith('شرح') ? 'normal' : 'nowrap' }}>
                      {c === 'مبلغ سپرده' ? numFmt(r[c]) : toFa(r[c])}
                    </td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
}

/* ═══════════ ۲. تعیین بستانکاری خزانه ═══════════ */
export function UpLevelPanel({ credit, onClose, onSaved }) {
  const [f, setF] = useState({ from: '', to: '', khz: '', amount: 0, count: 0 });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API(`/credits/${credit.id}/uplevel/`).then(j => {
      if (j.ok) setF(p => ({ ...p, from: j.from, to: j.to, amount: j.amount, count: j.count }));
      else setErr(j.error || j.msg);
    });
  }, [credit.id]);

  // خروج از «از» یا «تا» ⇒ جمع و تعداد دوباره
  const recalc = async () => {
    const j = await API(`/credits/${credit.id}/uplevel/?from=${f.from || 0}&to=${f.to || 0}`);
    if (j.ok) setF(p => ({ ...p, amount: j.amount, count: j.count }));
  };
  const confirm = async () => {
    setBusy(true);
    const j = await API(`/credits/${credit.id}/uplevel/confirm/`, { method: 'POST',
      body: JSON.stringify({ from: f.from, to: f.to, khz_credit_no: f.khz }) });
    setBusy(false);
    if (!j.ok) { setErr(j.error || j.msg); return; }
    if (j.skipped) return;           // شمارهٔ خزانهٔ صفر ⇒ بی‌صدا برمی‌گردد
    onSaved();
  };
  const num = k => e => setF(p => ({ ...p, [k]: e.target.value.replace(/[^0-9]/g, '') }));
  const field = (label, k, ro, onBlur) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
      <label style={st.label}>{label}</label>
      <input value={ro ? numFmt(f[k]) : f[k]} readOnly={ro} onChange={ro ? undefined : num(k)} onBlur={onBlur}
        style={{ ...st.input, background: ro ? '#f5f5f5' : '#fff' }} />
    </div>
  );
  return (
    <Shell title="تعیین بستانکاری خزانه" onClose={onClose} width="620px"
      footer={<><button onClick={onClose} style={st.btnGray}>بستن</button>
        <button onClick={confirm} disabled={busy} style={st.btn}>{busy ? '⏳...' : 'تایید'}</button></>}>
      {err && <div style={st.err}>{err}</div>}
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
        سال {toFa(credit.credit_year)} — ماه {toFa(credit.credit_month)}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        {field('بستانکاری دستگاه اجرایی از', 'from', false, recalc)}
        {field('تا', 'to', false, recalc)}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
        {field('شماره بستانکاری خزانه', 'khz', false)}
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        {field('جمع مبالغ سپرده', 'amount', true)}
        {field('تعداد ریز', 'count', true)}
      </div>
    </Shell>
  );
}
